<template>
  <v-dialog
    :value="value"
    max-width="800px"
    dark
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="text-h6">
        {{ formTitle }}
      </v-card-title>
      <v-divider />

      <v-card-text>
        <stallholder-form
          ref="stallholderForm"
          :form-data="formData"
          :event-settings="eventSettings"
          :inheritance-states="inheritanceStates"
          :selected-event="selectedEvent"
          :printing-list="printingList"
          :enforce-types="enforceTypes"
          @update-field="handleUpdateField"
          @inheritance-change="handleInheritanceChange"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn color="blue darken-1" text @click="cancel"> Cancel </v-btn>
        <v-btn color="blue darken-1" text @click="save"> Save </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import StallholderForm from "../forms/StallholderForm.vue";

export default {
  name: "StallholderDialog",
  components: {
    StallholderForm,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    stallholder: {
      type: Object,
      required: true,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    selectedEvent: {
      type: Object,
      required: true,
    },
    printingList: {
      type: Array,
      required: true,
    },
    enforceTypes: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      formData: {},
      eventSettings: {
        PaymentProfile: [],
        PrintingProfile: "",
        StandardDrinkLimitEnforce: "",
        StandardDrinkLimit: "",
        CardSurchargePercentage: "",
      },
      inheritanceStates: {
        isSelectPaymentProfile: true,
        isSelectPrintingProfile: true,
        isSelectStandardDrinkLimitEnforce: true,
        isSelectStandardDrinkLimit: true,
        isSelectCardSurchargePercentage: true,
      },
    };
  },
  computed: {
    formTitle() {
      return this.isEditing ? "Edit Stallholder" : "New Stallholder";
    },
  },
  watch: {
    stallholder: {
      handler(newVal) {
        console.log("StallholderDialog received stallholder data:", newVal);
        this.formData = { ...newVal };

        // Convert string booleans to actual booleans if needed
        if (this.formData.StandardDrinkLimitEnforce !== undefined) {
          console.log(
            "Original StandardDrinkLimitEnforce:",
            this.formData.StandardDrinkLimitEnforce,
            "type:",
            typeof this.formData.StandardDrinkLimitEnforce
          );

          // Convert string "true"/"false" to boolean if needed
          if (typeof this.formData.StandardDrinkLimitEnforce === "string") {
            if (
              this.formData.StandardDrinkLimitEnforce === "true" ||
              this.formData.StandardDrinkLimitEnforce === "Enforce"
            ) {
              this.formData.StandardDrinkLimitEnforce = true;
            } else if (
              this.formData.StandardDrinkLimitEnforce === "false" ||
              this.formData.StandardDrinkLimitEnforce === "Prompt"
            ) {
              this.formData.StandardDrinkLimitEnforce = false;
            }
          }

          console.log(
            "Converted StandardDrinkLimitEnforce:",
            this.formData.StandardDrinkLimitEnforce,
            "type:",
            typeof this.formData.StandardDrinkLimitEnforce
          );
        }

        this.$nextTick(() => {
          this.setupInheritanceStates();
        });
      },
      immediate: true,
      deep: true,
    },
    selectedEvent: {
      handler() {
        this.setupEventSettings();
        if (!this.isEditing) {
          this.$nextTick(() => {
            this.setupInheritanceStates();
          });
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    setupEventSettings() {
      if (this.selectedEvent) {
        this.eventSettings.StandardDrinkLimitEnforce =
          this.selectedEvent.StandardDrinkLimitEnforce;
        this.eventSettings.PaymentProfile = this.selectedEvent.PaymentProfile
          ? this.selectedEvent.PaymentProfile.join(", ")
          : "";
        this.eventSettings.PrintingProfile =
          this.selectedEvent.PrintingProfile || "";
        this.eventSettings.StandardDrinkLimit =
          this.selectedEvent.StandardDrinkLimit || "";
        this.eventSettings.CardSurchargePercentage =
          this.selectedEvent.CardSurchargePercentage || "";
      }
    },
    setupInheritanceStates() {
      if (this.isEditing) {
        // Compare actual values instead of using Inherit flags
        console.log("Comparing stallholder values to event values:");
        console.log(
          "PaymentProfile - Stallholder:",
          this.formData.PaymentProfile,
          "Event:",
          this.selectedEvent.PaymentProfile
        );
        console.log(
          "PrintingProfile - Stallholder:",
          this.formData.PrintingProfile,
          "Event:",
          this.selectedEvent.PrintingProfile
        );
        console.log(
          "StandardDrinkLimitEnforce - Stallholder:",
          this.formData.StandardDrinkLimitEnforce,
          "Event:",
          this.selectedEvent.StandardDrinkLimitEnforce
        );

        this.inheritanceStates.isSelectPaymentProfile = this.arraysEqual(
          this.formData.PaymentProfile,
          this.selectedEvent.PaymentProfile
        );
        this.inheritanceStates.isSelectPrintingProfile =
          this.formData.PrintingProfile === this.selectedEvent.PrintingProfile;
        this.inheritanceStates.isSelectCardSurchargePercentage =
          this.formData.CardSurchargePercentage ===
          this.selectedEvent.CardSurchargePercentage;
        this.inheritanceStates.isSelectStandardDrinkLimit =
          this.formData.StandardDrinkLimit ===
          this.selectedEvent.StandardDrinkLimit;
        this.inheritanceStates.isSelectStandardDrinkLimitEnforce =
          this.formData.StandardDrinkLimitEnforce ===
          this.selectedEvent.StandardDrinkLimitEnforce;

        console.log(
          "Inheritance states after comparison:",
          this.inheritanceStates
        );
      } else {
        // Reset to defaults for new stallholder and populate with event values
        this.inheritanceStates.isSelectPaymentProfile = true;
        this.inheritanceStates.isSelectPrintingProfile = true;
        this.inheritanceStates.isSelectStandardDrinkLimitEnforce = true;
        this.inheritanceStates.isSelectStandardDrinkLimit = true;
        this.inheritanceStates.isSelectCardSurchargePercentage = true;

        // Populate form data with event values since inheritance is true by default
        if (this.selectedEvent) {
          console.log(
            "Setting up new stallholder with event data:",
            this.selectedEvent
          );
          this.$set(
            this.formData,
            "PaymentProfile",
            this.selectedEvent.PaymentProfile || []
          );
          this.$set(
            this.formData,
            "PrintingProfile",
            this.selectedEvent.PrintingProfile || ""
          );
          this.$set(
            this.formData,
            "StandardDrinkLimitEnforce",
            this.selectedEvent.StandardDrinkLimitEnforce || ""
          );
          this.$set(
            this.formData,
            "StandardDrinkLimit",
            this.selectedEvent.StandardDrinkLimit || ""
          );
          this.$set(
            this.formData,
            "CardSurchargePercentage",
            this.selectedEvent.CardSurchargePercentage || ""
          );
          console.log("FormData after setup:", this.formData);
        }
      }
    },
    arraysEqual(arr1, arr2) {
      if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
      if (arr1.length !== arr2.length) return false;

      // Sort both arrays to compare regardless of order
      const sorted1 = [...arr1].sort();
      const sorted2 = [...arr2].sort();

      return sorted1.every((val, index) => val === sorted2[index]);
    },
    handleUpdateField(field, value) {
      this.$set(this.formData, field, value);
    },
    handleInheritanceChange(fieldKey, inheritKey, useEventSetting) {
      this.$set(this.formData, inheritKey, useEventSetting);

      // Update the inheritance state
      const stateKey = this.getInheritanceStateKey(inheritKey);
      if (stateKey) {
        this.$set(this.inheritanceStates, stateKey, useEventSetting);
      }

      if (useEventSetting) {
        // Set the field value to the event setting
        const eventValue = this.selectedEvent[fieldKey];
        this.$set(this.formData, fieldKey, eventValue);

        // Update event settings display
        if (fieldKey === "PaymentProfile" && Array.isArray(eventValue)) {
          this.eventSettings.PaymentProfile = eventValue.join(", ");
        } else {
          this.eventSettings[fieldKey] = eventValue;
        }
      }
    },
    getInheritanceStateKey(inheritKey) {
      const mapping = {
        PaymentProfileInherit: "isSelectPaymentProfile",
        PrintingProfileInherit: "isSelectPrintingProfile",
        StandardDrinkLimitEnforceInherit: "isSelectStandardDrinkLimitEnforce",
        StandardDrinkLimitInherit: "isSelectStandardDrinkLimit",
        CardSurchargePercentageInherit: "isSelectCardSurchargePercentage",
      };
      return mapping[inheritKey];
    },
    save() {
      if (this.$refs.stallholderForm.validate()) {
        // Ensure EventId is set
        this.formData.EventId = this.selectedEvent.EventId;

        // Format StandardDrinkLimitEnforce
        if (this.formData.StandardDrinkLimitEnforce) {
          this.formData.StandardDrinkLimitEnforce =
            this.formData.StandardDrinkLimitEnforce === "Enforce"
              ? "Enforce"
              : "Prompt";
        }

        this.$emit("save", { ...this.formData });
      }
    },
    cancel() {
      this.$emit("cancel");
    },
  },
};
</script>
